import { BellIcon, MultiSelectIcon, SyncIcon } from '@primer/octicons-react';
import { Box, Octicon, Tooltip } from '@primer/react';

import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../components/ui/button';
import DataTable from '../../components/ui/data-table';
import { useCommonContext } from '../../contexts/common';
import { useCommonSelectedHook } from '../../hooks/useCommonSelectedHook';
import { useLanguage } from '../../hooks/useLanguage';
import { ePageBaseStatus } from '../../models/ePageBaseStatus';
import { DiemDanhResponse } from '../../models/response/diem-danh/DiemDanhResponse';
import { IPhongDiemDanhResponse } from '../../models/response/diem-danh/IPhongDiemDanhResponse';
import { actions } from '../../state/actions/actionsWrapper';
import { RootState } from '../../state/reducers';
import DiemDanhAllModal from './DiemDanhAllModal';
import DiemDanhStatus from './DiemDanhStatus';
interface IDiemDanhProps {
    date: string,
    phongDiemDanh: IPhongDiemDanhResponse
}
const DiemDanh = (props: IDiemDanhProps) => {
    const { language } = useLanguage();
    const { translate } = useCommonContext();
    const { nam_hoc } = useCommonSelectedHook();
    const dispatch = useDispatch();
    const [showDiemDanhHocSinh, setShowDiemDanhHocSinh] = useState(false);
    const { sis_diemdanh_hocsinhs, status, is_show_diemdanh_modal } = useSelector(
        (state: RootState) => state.diemDanh
    );
    useEffect(() => {
        if (status === ePageBaseStatus.is_need_reload) {
            handleRefresh();
        }
    }, [status])
    useEffect(() => {
        handleRefresh();
    }, [props.date, props.phongDiemDanh])
    const handlePhongNamHocRefresh = useCallback(() => {
        const { phong_nam_hoc_id } = props.phongDiemDanh;
        if (phong_nam_hoc_id) {
            dispatch(
                actions.diemDanh.selectPhongNamHocStart({
                    noitru_phong_namhoc_id: props.phongDiemDanh.phong_nam_hoc_id,
                    nam_hoc: nam_hoc,
                    ngay_diem_danh: props.date,
                })
            );
        }
    }, [props.phongDiemDanh, props.date]);
    const handleRefresh = useCallback(() => {
        handlePhongNamHocRefresh()
    }, [props.phongDiemDanh, props.date]); 
 
    return (
        <Box>
            <DataTable
                columns={[
                    {
                        caption: translate('Base.Label.StudentCode'),
                        dataField: 'ma_hs',
                        width: "150px",
                        cellRender: (data: DiemDanhResponse) => {
                            return (
                                <Box>
                                    <Box sx={{
                                        color: "fg.muted"
                                    }}>{data.ma_hs}</Box>
                                </Box>
                            );
                        }
                    },
                    {
                        caption: translate('Base.Label.StudentName'),
                        dataField: 'ho_ten',
                        width: "200px",
                        cellRender: (data: DiemDanhResponse) => {
                            return (
                                <Box>
                                    <Box><b>{data.ho_ten}</b></Box>
                                </Box>
                            );
                        }
                    },
                    {
                        caption: translate('Base.Label.ClassName'),
                        dataField: 'ten_lop_hc',
                        width: "80px",
                        align: "center"
                    },

                    {
                        caption: translate('Base.Label.Status'),
                        dataField: 'sis_diemdanh_status_name',
                        width: "170px",
                        align: "center",
                        cellRender: (data: DiemDanhResponse) => {
                            return (
                                <DiemDanhStatus
                                    name={language === "en" ? data.sis_diemdanh_status_name_en : data.sis_diemdanh_status_name}
                                    id={data.sis_diemdanh_status_id}
                                />
                            );
                        }
                    },
                    {
                        dataField: 'gio_diem_danh',
                        caption: translate('DiemDanhHocSinhPage.Grid.AttendanceTime'),
                        width: "100px",
                        align: "center",
                        cellRender: (data: DiemDanhResponse) => {
                            const value = data.gio_diem_danh ?? ""
                            return (
                                <Box>
                                    <>{value ? (value.length > 5 ? value.substring(0, 5) : value) : ""}</>
                                </Box>
                            );
                        }
                    },
                    {
                        dataField: 'ghi_chu',
                        caption: translate('Base.Label.Note'),
                        minWidth: "150px",
                    },
                    {
                        caption: translate('Base.Label.NotifyStatus'),
                        dataField: 'app_notify_id',
                        width: "100px",
                        align: "center",
                        cellRender: (data: DiemDanhResponse) => {
                            return (
                                <>
                                    {data.app_notify_id > 0 &&
                                        <Tooltip direction="n" aria-label={translate('Base.Label.IsSentNotify')}>
                                            <Octicon icon={BellIcon} sx={{
                                                color: "success.fg"
                                            }} />
                                        </Tooltip>
                                    }
                                </>
                            );
                        }
                    }
                ]}
                height={`${window.innerHeight - 230}px`}
                data={props.phongDiemDanh ? sis_diemdanh_hocsinhs : []}
                title={props.phongDiemDanh.ten_phong}
                actionComponent={
                    <Box sx={{
                        display: "flex"
                    }}>
                        <Box>
                            <Button
                                size='medium'
                                id='btnDiemDanh'
                                variant='primary'
                                text='Base.Label.Attendance'
                                leadingVisual={MultiSelectIcon}
                                disabled={props.phongDiemDanh.phong_nam_hoc_id <= 0}
                                onClick={() => {
                                    dispatch(actions.diemDanh.showDiemDanhModal(true));
                                    dispatch(actions.diemDanh.changeDanhSachDiemDanh(sis_diemdanh_hocsinhs));
                                }}
                            />
                        </Box>

                        <Box sx={{ ml: 1 }}>
                            <Button
                                size='medium'
                                leadingVisual={SyncIcon}
                                text='Base.Label.Refresh'
                                onClick={() => handleRefresh()}
                                isLoading={status === ePageBaseStatus.is_loading}
                            />{' '}
                        </Box>
                    </Box>
                }
                isLoading={status === ePageBaseStatus.is_loading}
                searchEnable
                // selection={{
                //     mode: "multiple",
                //     onSelectionChanged: (keys) => {

                //     }
                // }}
            />
            {is_show_diemdanh_modal && (
                <DiemDanhAllModal
                    onClose={() => {
                        dispatch(actions.diemDanh.showDiemDanhModal(false));
                        dispatch(actions.diemDanh.changeDanhSachDiemDanh([]));
                    }}
                    phongDiemDanh={props.phongDiemDanh}
                    date={props.date}
                    data={sis_diemdanh_hocsinhs}
                />
            )}
           
        </Box>
    );
};

export default DiemDanh;